/**
 * 🎨 Testimonial Avatars Configuration (Mixed Styles with Light Skin)
 * 
 * 使用多種 DiceBear API 風格創造多樣化的角色頭像
 * 每個角色根據其性別、年齡、職業選擇最合適的風格
 * 所有角色統一使用白皮膚 (light skin tone)
 * 
 * 🎭 最新風格配對 (用戶指定 seed + 白皮膚)：
 * - 陳小姐: Avataaars (Caleb) - 白皮膚
 * - 林先生: Adventurer (Jocelyn) - 白皮膚
 * - 王女士: Avataaars (Liliana) - 白皮膚
 * - 張醫師: Open Peeps (Emery) - 白皮膚
 * - Emily: Big Ears (Aidan) - 白皮膚
 * - 黃老闆: Miniavs (Brooklynn) - 白皮膚
 */

/**
 * 🎭 角色頭像配置
 * 每個角色都有獨特的風格、seed 和配色
 */
export const testimonialAvatars = {
  // 1. 陳小姐 - 專業上班族女性 (Avataaars 風格 + Caleb seed)
  msChen: {
    id: 'ms-chen',
    name: '陳小姐',
    role: '專業上班族',
    persona: '28-32歲都市白領，注重效率與細節，溫和專業',
    style: 'avataaars',
    config: {
      seed: 'Caleb',
      backgroundColor: 'b6e3f4',  // 淡藍色 - 專業冷靜
      skinColor: 'ffdbb4'         // Avataaars 使用 skinColor (淺膚色)
    }
  },

  // 2. 林先生 - 專業寵物保姆 (Adventurer 風格 + Jocelyn seed)
  mrLin: {
    id: 'mr-lin',
    name: '林先生',
    role: '專業寵物保姆',
    persona: '28-35歲活力男性，熱愛動物，充滿活力',
    style: 'adventurer',
    config: {
      seed: 'Jocelyn',
      backgroundColor: 'ddd6fe',  // 淡紫色 - 活力親和
      skinColor: 'ffdbb4'         // Adventurer 使用 skinColor (淺膚色)
    }
  },

  // 3. 王女士 - 退休長輩 (Avataaars 風格 + Liliana seed)
  mrsWang: {
    id: 'mrs-wang',
    name: '王女士',
    role: '退休人士',
    persona: '60+歲慈祥長輩，有耐心，注重細節',
    style: 'avataaars',
    config: {
      seed: 'Liliana',
      backgroundColor: 'fce7f3',  // 淡粉紅 - 溫暖慈祥
      skinColor: 'ffdbb4'         // Avataaars 使用 skinColor (淺膚色)
    }
  },

  // 4. 張醫師 - 專業獸醫 (Open Peeps 風格 + Emery seed)
  drZhang: {
    id: 'dr-zhang',
    name: '張醫師',
    role: '獸醫師',
    persona: '35-45歲專業醫師，值得信賴，認真嚴謹',
    style: 'open-peeps',
    config: {
      seed: 'Emery',
      backgroundColor: 'e0e7ff',  // 淡靛藍 - 專業可靠
      skinColor: 'f9c9b6'         // Open Peeps 使用 skinColor (淺膚色)
    }
  },

  // 5. Emily - 大學生 (Big Ears 風格 + Aidan seed)
  emily: {
    id: 'emily',
    name: 'Emily',
    role: '大學生',
    persona: '18-22歲年輕學生，活潑開朗，充滿朝氣',
    style: 'big-ears',
    config: {
      seed: 'Aidan',
      backgroundColor: 'fed7aa',  // 淡橙色 - 青春活力
      skinColor: 'ffdbb4'         // Big Ears 使用 skinColor (淺膚色)
    }
  },

  // 6. 黃老闆 - 寵物店老闆 (Miniavs 風格 + Brooklynn seed)
  mrHuang: {
    id: 'mr-huang',
    name: '黃老闆',
    role: '寵物店老闆',
    persona: '40-50歲經驗豐富的店主，親切友善，有個性',
    style: 'miniavs',
    config: {
      seed: 'Brooklynn',
      backgroundColor: 'd1fae5',  // 淡綠色 - 親切友善
      skinColor: 'ffdbb4'         // Miniavs 使用 skinColor (淺膚色)
    }
  }
};

/**
 * 生成 DiceBear URL (支援多種風格 + 膚色參數)
 * @param {string} style - DiceBear 風格名稱
 * @param {Object} config - 頭像配置
 * @returns {string} - 完整的 DiceBear API URL
 */
const generateAvatarUrl = (style, config) => {
  const { seed, backgroundColor, skinColor, baseColor, facialHair } = config;
  
  // 基礎 URL
  let url = `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&backgroundColor=${backgroundColor}`;
  
  // 根據不同風格添加對應的膚色參數
  if (skinColor) {
    url += `&skinColor=${skinColor}`;
  }
  
  if (baseColor) {
    url += `&baseColor=${baseColor}`;
  }
  
  // Micah 風格支援 facialHair 參數（設為空陣列表示無鬍渣）
  if (facialHair !== undefined) {
    url += `&facialHair=${facialHair.join(',')}`;
  }
  
  return url;
};

/**
 * 獲取所有頭像的 URL 陣列
 * @returns {Array} - 包含所有頭像資訊的陣列
 */
export const getTestimonialAvatars = () => {
  return Object.values(testimonialAvatars).map(avatar => ({
    id: avatar.id,
    name: avatar.name,
    role: avatar.role,
    persona: avatar.persona,
    style: avatar.style,
    url: generateAvatarUrl(avatar.style, avatar.config)
  }));
};

/**
 * 根據 ID 獲取單個頭像 URL
 * @param {string} id - 頭像 ID
 * @returns {string} - 頭像 URL
 */
export const getAvatarById = (id) => {
  const avatar = Object.values(testimonialAvatars).find(a => a.id === id);
  return avatar ? generateAvatarUrl(avatar.style, avatar.config) : '';
};

/**
 * 預設匯出：所有頭像的 URL 列表
 */
export default getTestimonialAvatars();